import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Plus, Search, FileText, Loader2, ChevronLeft, ChevronRight, ArrowLeft,
  Building2, MapPin, Users,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/Sheet';
import { Logo } from '@/components/Logo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { useContracts, useCreateContract, useUpdateContract } from '@/hooks/useSiteContracts';
import { ContractForm } from './ContractForm';
import { formatSar } from '@/lib/trip-math';
import type { SiteContract, ContractStatus, TripType } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { ClientsContractsTabs } from '@/components/ClientsContractsTabs';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';

type StatusFilter = 'all' | ContractStatus;
type TripTypeFilter = 'all' | TripType;

const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'draft', 'on_hold', 'expired', 'terminated'];

export default function ContractsPage() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const isRtl = i18n.language === 'ar' || i18n.language === 'ur';

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [tripTypeFilter, setTripTypeFilter] = useState<TripTypeFilter>('all');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SiteContract | null>(null);

  useEffect(() => {
    const tm = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(tm);
  }, [searchInput]);

  const params = useMemo(() => ({
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
    tripType: tripTypeFilter === 'all' ? undefined : tripTypeFilter,
    page, pageSize: 25,
  }), [search, statusFilter, tripTypeFilter, page]);

  const { data, isLoading, isError, error, refetch } = useContracts(params);
  const createMutation = useCreateContract();
  const updateMutation = useUpdateContract();

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleCreate = async (input: Parameters<typeof createMutation.mutateAsync>[0]) => {
    await createMutation.mutateAsync(input);
    setCreating(false);
  };
  const handleUpdate = async (input: Parameters<typeof updateMutation.mutateAsync>[0]['input']) => {
    if (!editing) return;
    await updateMutation.mutateAsync({ id: editing.id, input });
    setEditing(null);
  };

  const displayName = user?.fullNameEn ?? user?.fullName ?? '';

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
            <Logo size={36} variant="mark" />
            <div className="hidden sm:block">
              <div className="font-display text-lg leading-none text-gradient-gold">JASR HUB</div>
              <div className="text-[11px] text-muted-foreground">{t('app.tagline')}</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher /><ThemeToggle />
            <div className="ms-2 ps-2 border-s border-border">
              <Button variant="ghost" size="sm" onClick={logout}>{t('auth.logout')}</Button>
            </div>
          </div>
        </div>
      </header>

      <ClientsContractsTabs />

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <Link to="/dashboard" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2">
              <ArrowLeft className={`h-3 w-3 ${isRtl ? 'rotate-180' : ''}`} />{t('common.backToDashboard')}
            </Link>
            <div className="flex items-center gap-3">
              <FileText className="h-7 w-7 text-gold" />
              <h1 className="font-display text-3xl text-foreground">{t('dashboard.modules.contracts')}</h1>
            </div>
            <p className="text-sm text-muted-foreground mt-1">{t('contracts.subtitle')}</p>
          </div>
          <Button variant="gold" size="lg" onClick={() => setCreating(true)} className="shrink-0">
            <Plus className="h-4 w-4" />{t('contracts.addContract')}
          </Button>
        </div>

        <div className="bg-card border border-border rounded-lg p-4 mb-4 space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('contracts.searchPlaceholder')} className="ps-10" />
            </div>
            <select value={tripTypeFilter} onChange={(e) => { setTripTypeFilter(e.target.value as TripTypeFilter); setPage(1); }}
              className="h-11 rounded-md border border-input bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <option value="all">{t('contracts.filter.allTripTypes')}</option>
              <option value="water">{t('contracts.tripType.water')}</option>
              <option value="sewage">{t('contracts.tripType.sewage')}</option>
              <option value="container">{t('contracts.tripType.container')}</option>
              <option value="flatbed">{t('contracts.tripType.flatbed')}</option>
              <option value="pickup">{t('contracts.tripType.pickup')}</option>
              <option value="other">{t('contracts.tripType.other')}</option>
            </select>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {STATUS_FILTERS.map((s) => (
              <button key={s} onClick={() => { setStatusFilter(s); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  statusFilter === s ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'
                }`}>
                {t(`contracts.filter.${s}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : isError ? (
            <div className="py-16 text-center">
              <p className="text-destructive">{(error as Error)?.message ?? 'Error'}</p>
              <Button variant="outline" onClick={() => refetch()} className="mt-4">{t('common.retry')}</Button>
            </div>
          ) : items.length === 0 ? (
            <div className="py-20 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl text-foreground">
                {search || statusFilter !== 'all' || tripTypeFilter !== 'all' ? t('contracts.noResults') : t('contracts.empty')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {search || statusFilter !== 'all' || tripTypeFilter !== 'all' ? t('contracts.tryDifferent') : t('contracts.emptyDescription')}
              </p>
              {!search && statusFilter === 'all' && tripTypeFilter === 'all' && (
                <Button variant="gold" onClick={() => setCreating(true)} className="mt-6">
                  <Plus className="h-4 w-4" />{t('contracts.addFirstContract')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.code')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.parties')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.tripType')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.rate')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.investors')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.dates')}</th>
                      <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{t('contracts.column.status')}</th>
                      <th className="text-end px-4 py-3 w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((c) => {
                      const investorCount = c.investorRates?.length ?? 0;
                      return (
                        <tr key={c.id} onClick={() => setEditing(c)}
                          className="border-b border-border last:border-b-0 hover:bg-muted/40 transition-colors cursor-pointer">
                          <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{c.code}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5 text-foreground">
                              <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="font-medium">{c.site.nameEn}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-muted-foreground text-xs mt-1">
                              <Building2 className="h-3 w-3" />
                              <span>{c.client.nameEn}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm">{t(`contracts.tripType.${c.tripType}`)}</span>
                            <div className="text-[11px] text-muted-foreground">
                              {t(`contracts.rateUnit.${c.rateUnit}`)} · {c.targetQty}
                              {c.rateUnit === 'per_m3' ? ' m³' : c.rateUnit === 'per_ton' ? ' t' : c.rateUnit === 'per_km' ? ' km' : ''}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-mono text-sm tabular-nums">{formatSar(c.clientRateAtTarget)}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {t('contracts.column.driver')}: {formatSar(c.driverRateAtTarget, { showCurrency: false })}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {investorCount === 0 ? (
                              <span className="text-xs text-muted-foreground italic">{t('contracts.column.noInvestors')}</span>
                            ) : (
                              <div className="inline-flex items-center gap-1.5">
                                <Users className="h-3 w-3 text-muted-foreground" />
                                <span className="text-sm">{investorCount}</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs">
                            <div className="text-muted-foreground">{new Date(c.effectiveFrom).toLocaleDateString('en-CA')}</div>
                            {c.effectiveTo ? (
                              <div className="text-muted-foreground">→ {new Date(c.effectiveTo).toLocaleDateString('en-CA')}</div>
                            ) : (
                              <div className="text-emerald-600 dark:text-emerald-400 text-[11px]">{t('contracts.column.openEnded')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={
                              c.status === 'active' ? 'active' :
                              c.status === 'draft' || c.status === 'on_hold' ? 'gold' :
                              'inactive'
                            }>
                              {t(`contracts.status.${c.status}`)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-end" onClick={(e) => e.stopPropagation()}>
                            <RowDeleteButton
                              endpoint={`/api/v1/site-contracts/${c.id}`}
                              entityKind="contract"
                              label={c.code}
                              onDeleted={() => refetch()}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-3 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 bg-muted/20">
                <p className="text-sm text-muted-foreground">
                  {t('common.showingRange', { from: (page - 1) * 25 + 1, to: Math.min(page * 25, total), total })}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                  <span className="px-3 text-sm tabular-nums">{page} / {totalPages}</span>
                  <Button variant="outline" size="icon" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
                    <ChevronRight className={`h-4 w-4 ${isRtl ? 'rotate-180' : ''}`} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8">
          {displayName} · {t(`dashboard.role.${user?.role ?? ''}`, { defaultValue: user?.role ?? '' })}
        </p>
      </main>

      <Sheet open={creating} onOpenChange={setCreating}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{t('contracts.addContract')}</SheetTitle>
            <SheetDescription>{t('contracts.addContractDescription')}</SheetDescription>
          </SheetHeader>
          <ContractForm onSubmit={handleCreate} onCancel={() => setCreating(false)} submitLabel={t('contracts.create')} />
        </SheetContent>
      </Sheet>

      <Sheet open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing?.code ?? ''}</SheetTitle>
            <SheetDescription>
              {editing?.site.nameEn} · {editing?.client.nameEn}
            </SheetDescription>
          </SheetHeader>
          <ContractForm initial={editing} onSubmit={handleUpdate} onCancel={() => setEditing(null)} submitLabel={t('common.save')} />
        </SheetContent>
      </Sheet>
    </div>
  );
}
